import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

// CSS-var driven primary tones — TenantContext.applyTheme writes
// `--brand-primary-base` / `--brand-primary-active` per-tenant. Sprint G
// Phase 6: admin LoginForm no longer hard-codes Agciceyim burgundy in
// inline styles. The tenant-A fallback values keep the form looking
// identical until the structured tenant theme lands.
const PRIMARY = 'var(--brand-primary-base, #5F162A)'
const PRIMARY_DARK = 'var(--brand-primary-active, #4a1120)'
// CSS color-mix with transparency for shadows. Fallback to baked HEX+alpha
// when --brand-primary-base is unavailable (older browsers / null tenant).
const PRIMARY_GLOW_4 = 'color-mix(in srgb, var(--brand-primary-base, #5F162A) 10%, transparent)'
const PRIMARY_GLOW_8 = 'color-mix(in srgb, var(--brand-primary-base, #5F162A) 40%, transparent)'
const PRIMARY_GLOW_2 = 'color-mix(in srgb, var(--brand-primary-base, #5F162A) 20%, transparent)'

export const LoginForm: React.FC = () => {
  const [emailOrPhone, setEmailOrPhone] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [focusedField, setFocusedField] = useState<'id' | 'pw' | null>(null)
  const [btnHover, setBtnHover] = useState(false)
  const { login } = useAuth()
  const tenant = useTenant()
  // Logo: prefer tenant-supplied horizontal logo at /tenants/<slug>/...,
  // fall back to admin-default-logo.svg (neutral "Tenant" wordmark).
  const tenantLogo = tenant?.brand?.assets?.logoHorizontal
  const logoUrl = tenant?.assetSlug && tenantLogo
    ? `/tenants/${tenant.assetSlug}/${tenantLogo}`
    : '/admin-default-logo.svg'
  const brandName =
    tenant?.brand?.name?.az ?? tenant?.brand?.name?.en ?? tenant?.slug ?? 'Tenant'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      await login(emailOrPhone, password)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Giriş xətası')
    } finally {
      setIsLoading(false)
    }
  }

  const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    height: 48,
    padding: '0 16px',
    fontSize: 15,
    color: '#1a1a1a',
    background: '#ffffff',
    border: `1px solid ${focused ? PRIMARY : '#e5e5e5'}`,
    borderRadius: 10,
    outline: 'none',
    boxShadow: focused ? `0 0 0 4px ${PRIMARY_GLOW_4}` : 'none',
    transition: 'border-color 150ms, box-shadow 150ms',
    boxSizing: 'border-box',
  })

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${PRIMARY_GLOW_4} 0%, transparent 60%), linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)`,
      }}
    >
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ marginBottom: 40, display: 'flex', justifyContent: 'center' }}>
          <img src={logoUrl} alt={brandName} style={{ height: 32, width: 'auto' }} />
        </div>

        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: '#1a1a1a',
              lineHeight: 1.2,
            }}
          >
            Xoş gəldiniz
          </h1>
          <p
            style={{
              margin: '8px 0 0 0',
              fontSize: 14,
              color: '#737373',
              lineHeight: 1.5,
            }}
          >
            İdarəetmə panelinə daxil olun
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div
              role="alert"
              style={{
                padding: '12px 14px',
                fontSize: 14,
                color: '#991b1b',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 10,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label
              htmlFor="identifier"
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: '#404040',
              }}
            >
              Telefon və ya email
            </label>
            <input
              id="identifier"
              type="text"
              value={emailOrPhone}
              onChange={(e) => setEmailOrPhone(e.target.value)}
              onFocus={() => setFocusedField('id')}
              onBlur={() => setFocusedField(null)}
              placeholder="+994 50 250 77 29"
              autoComplete="username"
              required
              style={inputStyle(focusedField === 'id')}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label
              htmlFor="password"
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: '#404040',
              }}
            >
              Şifrə
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('pw')}
                onBlur={() => setFocusedField(null)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                style={{ ...inputStyle(focusedField === 'pw'), paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Şifrəni gizlət' : 'Şifrəni göstər'}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: '#a3a3a3',
                  cursor: 'pointer',
                  borderRadius: 6,
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              marginTop: 8,
              width: '100%',
              height: 48,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontSize: 15,
              fontWeight: 500,
              color: '#ffffff',
              background: isLoading ? PRIMARY : btnHover ? PRIMARY_DARK : PRIMARY,
              border: 'none',
              borderRadius: 10,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              boxShadow: btnHover
                ? `0 10px 24px -8px ${PRIMARY_GLOW_8}`
                : `0 4px 12px -2px ${PRIMARY_GLOW_2}`,
              transition: 'background 150ms, box-shadow 200ms',
            }}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Daxil olunur...
              </>
            ) : (
              'Daxil ol'
            )}
          </button>
        </form>

        <p
          style={{
            margin: '40px 0 0 0',
            fontSize: 12,
            color: '#a3a3a3',
            textAlign: 'center',
          }}
        >
          © {new Date().getFullYear()} {brandName}
        </p>
      </div>
    </div>
  )
}
